import { useContext, useEffect, useState } from "react";
import ReactGA from "react-ga4";
import RequestQuoteFormContext from "./RequestQuoteFormContext";
import Echo from "laravel-echo";
import Pusher from "pusher-js";

window.Pusher = Pusher;

const TOTAL = 60;
const R = 54;
const CIRCUMFERENCE = 2 * Math.PI * R;

const Done = () => {
	const { next, quoteId } = useContext(RequestQuoteFormContext);
	const [seconds, setSeconds] = useState(TOTAL);
	const [status, setStatus] = useState("waiting"); // waiting | connected | busy

	useEffect(() => {
		if (!quoteId) {
			console.error("No Quote ID found in context.");
			return;
		}

		const echo = new Echo({
			broadcaster: "pusher",
			key: process.env.REACT_APP_PUSHER_APP_KEY,
			cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
			forceTLS: true,
		});

		echo.channel(`quotes.${quoteId}`).listen(".quote.updated", (data) => {
			if (data.status === "accepted") {
				setStatus("connected");
				ReactGA.event({ category: "Quote Form", action: "agent_connected" });
			}
		});

		const timer = setInterval(() => {
			setSeconds((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					setStatus((current) => {
						if (current !== "connected") {
							ReactGA.event({ category: "Quote Form", action: "agent_busy" });
							return "busy";
						}
						return "connected";
					});
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => {
			clearInterval(timer);
			echo.leaveChannel(`quotes.${quoteId}`);
		};
	}, [quoteId]);

	const progress = seconds / TOTAL;
	const dashOffset = CIRCUMFERENCE * (1 - progress);

	const waitingUI = (
		<div className="flex flex-col items-center gap-6">
			<div className="relative w-36 h-36">
				<svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
					{/* Track */}
					<circle
						cx="60"
						cy="60"
						r={R}
						fill="none"
						stroke="#e2e8f0"
						strokeWidth="6"
					/>
					{/* Progress */}
					<circle
						cx="60"
						cy="60"
						r={R}
						fill="none"
						stroke="#2abaf7"
						strokeWidth="6"
						strokeLinecap="round"
						strokeDasharray={CIRCUMFERENCE}
						strokeDashoffset={dashOffset}
						className="transition-[stroke-dashoffset] duration-1000 ease-linear"
					/>
				</svg>
				<div className="absolute inset-0 flex flex-col items-center justify-center">
					<span className="text-4xl font-medium text-slate-900 tabular-nums leading-none">
						{seconds}
					</span>
					<span className="text-xs text-slate-400 mt-1">sec</span>
				</div>
			</div>

			<div className="text-center">
				<p className="text-sm font-medium text-slate-900">
					Connecting you to an agent
				</p>
				<p className="text-xs text-slate-400 mt-1">
					Please wait, we'll be with you shortly
				</p>
			</div>
		</div>
	);

	// — Connected state
	const connectedUI = (
		<div className="flex flex-col items-center gap-6">
			<div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
				<svg
					className="w-9 h-9 text-emerald-500"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M5 13l4 4L19 7"
					/>
				</svg>
			</div>
			<div className="text-center">
				<p className="text-sm font-medium text-slate-900">You're connected</p>
				<p className="text-xs text-slate-400 mt-1">
					An agent has accepted your request
				</p>
			</div>
		</div>
	);

	// — Busy state
	const busyUI = (
		<div className="flex flex-col items-center gap-6">
			<div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center">
				<svg
					className="w-9 h-9 text-amber-400"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
					/>
				</svg>
			</div>
			<div className="text-center">
				<p className="text-sm font-medium text-slate-900">
					All agents are busy
				</p>
				<p className="text-xs text-slate-400 mt-1">
					We've received your request and will contact you as soon as possible
				</p>
			</div>
		</div>
	);

	return (
		<div className="flex flex-col gap-8 py-4">
			{status === "waiting" && waitingUI}
			{status === "connected" && connectedUI}
			{status === "busy" && busyUI}

			{status !== "waiting" && (
				<button
					onClick={next}
					className="w-full py-2.5 bg-brand-500 ha:bg-brand-600 active:scale-[0.99] text-white text-sm font-medium tracking-wide transition-all rounded-sm"
				>
					Close
				</button>
			)}
		</div>
	);
};

export default Done;
